import { AxiosResponse } from 'axios';
import { Uuid } from 'resources/index';

import RestAPI from 'components/Common/restApi';

import RedundancyApi from './Redundancy/RedundancyApi';
import { VoiceToken, WorkerCapabilityToken } from './types';

export interface UpdateRecordingProps {
  callSid: string;
  status: 'paused' | 'in-progress';
  displayDirection: string | undefined;
}
interface UpdateConferenceRecordingProps {
  callSid: string;
  status: 'paused' | 'in-progress';
}

interface InitiateTransferProps {
  childCallSid?: string;
  parentCallSid: string;
  to: string;
  childCallLabel: string;
  parentCallLabel: string;
  addedCallLabel: string;
  isRecordingActive: boolean;
  twilioPhoneNumberUuid?: string;
}

interface DisconnectParticipantProps {
  parentCallSid: string;
  participantLabel: string;
}

export default class VoiceApi {
  static async token(): Promise<AxiosResponse<VoiceToken> | undefined> {
    try {
      const token = await RestAPI.post('/alpha/voice/credentials/token');
      return token;
    } catch (error: any) {
      if (error.response?.status !== 403) {
        const redundantToken = await RedundancyApi.token();
        return redundantToken;
      }
    }
  }

  static workerCapabilityToken(): Promise<AxiosResponse<WorkerCapabilityToken>> {
    return RestAPI.post('/alpha/voice/credentials/worker_capability_token');
  }

  static dismissCallNotification(callLogUuid: Uuid<'vcl'>) {
    return RestAPI.postWithFormData('/alpha/voice/notifications/dismiss_call', { call_log_uuid: callLogUuid });
  }

  static async updateRecording({ callSid, status, displayDirection }: UpdateRecordingProps) {
    try {
      const response = await RestAPI.updateWithFormData('/alpha/voice/recordings', {
        call_sid: callSid,
        status,
        display_direction: displayDirection,
      });

      return response;
    } catch (error) {
      const redundantResponse = await RedundancyApi.updateRecording({ callSid, status, displayDirection });
      return redundantResponse;
    }
  }

  static updateConferenceRecording({ callSid, status }: UpdateConferenceRecordingProps) {
    return RestAPI.updateWithFormData('/alpha/voice/conferences/update_recording', { call_sid: callSid, status });
  }

  static initiateTransfer({
    childCallSid,
    parentCallSid,
    to,
    childCallLabel,
    parentCallLabel,
    addedCallLabel,
    isRecordingActive,
    twilioPhoneNumberUuid,
  }: InitiateTransferProps) {
    return RestAPI.postWithFormData('/alpha/voice/conferences/initiate_transfer', {
      child_call_sid: childCallSid,
      parent_call_sid: parentCallSid,
      to,
      child_call_label: childCallLabel,
      parent_call_label: parentCallLabel,
      added_call_label: addedCallLabel,
      record_conference: isRecordingActive.toString(),
      twilio_phone_number_uuid: twilioPhoneNumberUuid,
    });
  }

  static disconnectParticipant({ parentCallSid, participantLabel }: DisconnectParticipantProps) {
    return RestAPI.postWithFormData('/alpha/voice/conferences/remove_participant', {
      participant_label: participantLabel,
      parent_call_sid: parentCallSid,
    });
  }
}
